import React from 'react';
import type { NavigateFunction } from 'react-router-dom';
import type { ErrorInfo } from 'scbk-services';
import { AppError, ErrorPageComponent, RestResponseError, NavigateOptions } from 'scbk-services';

export interface MenuOptions extends NavigateOptions {}

/**
 * 전역 ErrorBoundary Props
 */
interface GlobalErrorBoundaryProps {
    children: React.ReactNode;
    errorPage: ErrorPageComponent;
    goMenu?: (pageId: string, options?: MenuOptions) => Promise<void>;
    navigate?: NavigateFunction;
    setErrorInfo?: (error: ErrorInfo) => void;
}

/**
 * 전역 ErrorBoundary 상태
 */
interface GlobalErrorBoundaryState {
    error: Error | null;
}

/**
 * 전역 ErrorBoundary 컴포넌트
 * TODO: 다국어 처리 어떻게 할건지 확인해봐야함
 */
export class GlobalErrorBoundary extends React.Component<
    GlobalErrorBoundaryProps,
    GlobalErrorBoundaryState
> {
    constructor(props: any) {
        super(props);
        this.state = { error: null };
    }

    /**
     *
     * @param error
     * @returns
     */
    static getDerivedStateFromError(error: unknown) {
        console.error('### error 감지 : ', error);
        if (error instanceof AppError) {
            return { error };
        }

        if (error instanceof RestResponseError) {
            return { error };
        }

        if (error instanceof Error) {
            return {
                error: new AppError('예기치않은 오류가 발생했습니다.', {
                    code: 'RUNTIME_ERROR',
                    errorMessage: error.message,
                }),
            };
        }

        return {
            error: new AppError('예기치않은 오류가 발생했습니다.', {
                code: 'RENDERING_ERROR',
                errorMessage: '',
            }),
        };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        console.log('# [GlobalErrorBoundary]', error, errorInfo);
    }

    reset = () => {
        this.setState({ error: null });
    };

    render(): React.ReactNode {
        const { error } = this.state;
        const { errorPage: ErrorPage } = this.props;
        if (error && (error instanceof AppError || error instanceof RestResponseError)) {
            console.log('### error : ', error?.code);
            return (
                <ErrorPage
                    errorCode={error?.code ?? ''}
                    errorMessage={error?.errorMessage ?? ''}
                    reset={this.reset}
                />
            );
        }

        return this.props.children;
    }
}
